<template>
  <div class="rounded-md bg-neutral-secondary px-2 h-full">
    <div
      class="flex items-center justify-between sticky top-0 bg-neutral-secondary h-[48px] overflow-hidden"
      :class="[expandedNews ? 'pt-2' : 'py-2']"
    >
      <NText weight="bold" variant="caption1" class="ms-2">
        {{ t('modules.news.title') }}
      </NText>

      <div class="inline-flex justify-center items-center">
        <div class="relative" @click.stop="openNewsFilter">
          <NButtonIcon size="small" color="gray" variant="plain">
            <NegarFilterIcon size="20" />
          </NButtonIcon>

          <span
            v-if="newsFilterCount"
            class="absolute w-3 h-3 right-1.5 top-1.5 bg-brand-primary rounded-full"
          >
            <NText
              variant="overline"
              class="relative -top-1.5 text-neutral-quaternary"
            >
              {{ newsFilterCount }}
            </NText>
          </span>
        </div>

        <NButtonIcon
          v-if="expandedNews"
          v-gid="{
            module: 'news',
            screen: 'home',
            section: 'news',
            component: 'collapseButton',
          }"
          size="small"
          color="gray"
          variant="plain"
          class="transition duration-300"
          @click="updateExpandedNewsSection"
        >
          <NegarMinusIcon />
        </NButtonIcon>

        <NButtonIcon
          v-else
          v-gid="{
            module: 'news',
            screen: 'home',
            section: 'news',
            component: 'expandButton',
          }"
          size="small"
          color="gray"
          variant="plain"
          class="transition duration-300"
          @click="updateExpandedNewsSection"
        >
          <NegarArrowUpIcon />
        </NButtonIcon>
      </div>
    </div>

    <div
      v-if="expandedNews"
      ref="loadMoreRef"
      class="text-start h-[calc(100%-48px)] overflow-y-auto"
    >
      <ComponentState :fetch-status="newsStatus">
        <template #loading>
          <NewsSkeleton />
        </template>

        <template #error>
          <Error class="p-2" @retry="getNews" />
        </template>

        <div class="flex flex-wrap">
          <template v-for="(value, key) in route.query" :key="key">
            <NCheap
              v-if="
                isPortfolioFilterSelected(key) &&
                VALID_NEWS_FILTER_KEYS.includes(key) &&
                value !== defaultFilters[key]
              "
              :model-value="true"
              closable
              color="green"
              class="ml-2 whitespace-nowrap mt-2"
              :label="getFilterLabel({ key, value })"
              @close="deleteFilterByKey(key)"
            >
            </NCheap>
          </template>
        </div>

        <template v-if="newsWithoutFakeData.length">
          <NewsItem
            v-for="(item, index) of newsWithoutFakeData"
            :key="`${item.id}${index}`"
            v-gid="{
              module: 'news',
              screen: 'home',
              section: 'news',
              component: 'newsItem',
            }"
            :news="item"
            @click="openNewsItemDialog(item)"
          />
        </template>

        <EmptyList
          v-else
          :title="t('common.noInformationAvailable')"
          title-class="text-center mt-3"
          class="h-[calc(100%-70px)] flex flex-col justify-center items-center rounded-md my-3"
        >
          <template #image>
            <Image src="empty_box" alt="not_found" class="w-28 h-max" />
          </template>
        </EmptyList>
      </ComponentState>

      <NewsSkeleton v-if="isFetchingNextPage && newsWithoutFakeData.length" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import {
  NegarArrowUpIcon,
  NegarFilterIcon,
  NegarMinusIcon,
} from '@negar/icons';
import { useLiveNewsQuery } from '@core/news';
import { queryStringToJson, toShortDate } from '@libs/utils';
import { useRoute, useRouter } from 'vue-router';
import { VALID_NEWS_FILTER_KEYS } from '@core/news';
import {
  useLivePortfolioQuery,
  useLiveVirtualPortfolioQuery,
} from '@core/portfolio';

import { useNewsDialogs } from '@/modules/news';
import { useUserSettings } from '@/modules/user';

import { toJalali } from '@/components/Datepicker/convertor';
import EmptyList from '@/components/EmptyList.vue';
import Error from '@/components/Error.vue';
import Image from '@/components/Image.vue';
import ComponentState from '@/components/ComponentState.vue';

import NewsItem from './NewsItem.vue';
import NewsSkeleton from './NewsSkeleton.vue';

const props = defineProps<{ expandedNews: boolean }>();

const emit = defineEmits<{
  (e: 'update:expandedNews', value: boolean): boolean;
}>();

const { t } = useI18n();
const route = useRoute();
const router = useRouter();

const { openNewsFilter, openNewsItemDialog } = useNewsDialogs();

const { symbols, isLoadingSymbols } = useLivePortfolioQuery({
  listen: false,
});

const { symbols: virtualPortfolioSymbols, isLoadingVirtualPortfolioSymbols } =
  useLiveVirtualPortfolioQuery({
    immediate: computed(() => Boolean(route.query.virtualPortfolio)),
    listen: false,
  });

const { updateLayoutSettings } = useUserSettings();

const instruments = computed(() => {
  const { instruments = [] }: { instruments?: string[] | string } = route.query;

  return queryStringToJson(instruments);
});

const DEFAULT_FROM_DATE = '2011-03-21';
const DEFAULT_TO_DATE = toShortDate(new Date());

const defaultFilters = {
  fromDate: DEFAULT_FROM_DATE,
  endDate: DEFAULT_TO_DATE,
};

const query = computed(() => {
  const {
    fromDate = DEFAULT_FROM_DATE,
    endDate = DEFAULT_TO_DATE,
    instruments = [],
    portfolio = false,
    virtualPortfolio = false,
  } = route.query;

  const instrumentsList = queryStringToJson(
    instruments as string[] | undefined
  );

  const filteredInstruments: string[] = [];

  if (!portfolio || !virtualPortfolio) {
    for (const instrument of instrumentsList) {
      filteredInstruments.push(instrument.value);
    }
  }

  if (portfolio) {
    symbols.value.forEach((symbol) =>
      filteredInstruments.push(symbol.instrumentIsin as string)
    );
  }

  if (virtualPortfolio) {
    virtualPortfolioSymbols.value.forEach((symbol) =>
      filteredInstruments.push(symbol.instrumentIsin as string)
    );
  }

  return {
    fromDate: fromDate,
    toDate: endDate,
    isins: filteredInstruments,
  };
});

const {
  newsWithoutFakeData,
  isFetchingNextPage,
  getNews,
  loadMoreRef,
  newsStatus,
} = useLiveNewsQuery({
  params: query,
  immediate: computed(
    () =>
      !(Boolean(route.query.portfolio) && isLoadingSymbols.value) ||
      !(
        Boolean(route.query.virtualPortfolio) &&
        isLoadingVirtualPortfolioSymbols.value
      )
  ),
});

function getJalaliDate(value) {
  const date = new Date(value);
  const [year, month, day] = toJalali(
    date.getFullYear(),
    date.getMonth(),
    date.getDate()
  );

  return `${year}/${month + 1}/${day}`;
}

function getFilterLabel({ key, value }) {
  return {
    instruments:
      instruments.value.length === 1
        ? instruments.value[0].label
        : `${t('common.symbol')} (${instruments.value.length})`,
    fromDate: `${t('common.from')} ${getJalaliDate(value)}`,
    endDate: `${t('common.to')} ${getJalaliDate(value)}`,
    portfolio: t('modules.news.myPortfolio'),
    virtualPortfolio: t('modules.news.myVirtualPortfolio'),
  }[key];
}

function deleteFilterByKey(key) {
  const newQueries = { ...route.query };

  delete newQueries[key];

  updateURL(newQueries);
}

function updateURL(newQueries) {
  router.replace({
    path: route.path,
    query: {
      ...newQueries,
    },
  });
}

const newsFilterCount = computed(
  () =>
    Object.keys(route.query).filter(
      (key) =>
        VALID_NEWS_FILTER_KEYS.includes(key) &&
        route.query[key] !== defaultFilters[key]
    ).length
);

const isPortfolioFilterSelected = (key: string) => {
  return (
    !(Boolean(route.query.portfolio) && key === 'instruments') ||
    !(Boolean(route.query.virtualPortfolio) && key === 'instruments')
  );
};

const updateExpandedNewsSection = () => {
  updateLayoutSettings('expandedNews', !props.expandedNews);

  emit('update:expandedNews', !props.expandedNews);
};
</script>
